/**
 * Client helpers for the `verify-company-claim` edge function (Phase 116.2).
 *
 * Pairs with `useClaimCompany` in `./company-directory.ts` — the founder
 * must complete `requestEmailCode` → `submitEmailCode` flow first to obtain
 * a `verificationId`, which the claim hook then verifies server-side.
 */

import { callEdgeFunction } from './edge-functions';
import { claimRefusalFor } from '@/lib/company/claim-refusals';

/**
 * A non-2xx reply → the error to throw. A lifecycle refusal (119-03: 409
 * `company_closed` / `erasure_hold`, sent BEFORE any code is issued or mailed)
 * becomes a typed ClaimRefusalError carrying its own words; anything else keeps
 * today's behaviour. Branches on the code, never the message.
 */
function refusal(error: string, code: string | undefined): Error {
  return claimRefusalFor(code) ?? new Error(error);
}

export interface RequestEmailCodeResponse {
  success: boolean;
  verificationId: string;
  expiresInMinutes: number;
}

export interface SubmitEmailCodeResponse {
  success: boolean;
  verificationId: string;
  directoryId: string;
}

/**
 * Request a 6-digit OTP for the directory claim. Server validates that
 * the email's domain matches the directory's extracted domain (or the
 * originating submission's contact_email) and rejects generic free-mail
 * providers. The OTP is emailed via the `company-claim-verify` template.
 */
export async function requestEmailCode(args: {
  directoryId: string;
  email: string;
}): Promise<RequestEmailCodeResponse> {
  const { data, error, code } = await callEdgeFunction<RequestEmailCodeResponse>(
    'verify-company-claim',
    { action: 'request_email_code', ...args },
  );
  if (error) throw refusal(error, code);
  if (!data) throw new Error('Empty response from verify-company-claim');
  return data;
}

/**
 * Redeem the OTP. On success the verification's `status` flips to
 * `verified`, which unblocks `useClaimCompany`.
 */
export async function submitEmailCode(args: {
  verificationId: string;
  code: string;
}): Promise<SubmitEmailCodeResponse> {
  const { data, error, code } = await callEdgeFunction<SubmitEmailCodeResponse>(
    'verify-company-claim',
    { action: 'submit_email_code', ...args },
  );
  if (error) throw refusal(error, code);
  if (!data) throw new Error('Empty response from verify-company-claim');
  return data;
}

export interface LinkedInProofResponse {
  success: boolean;
  verificationId: string;
  directoryId: string;
  matchMode: 'slug' | 'name';
}

/**
 * Phase 116-2.1 — Employee-proof via LinkedIn.
 *
 * Server pulls the founder's LinkedIn profile URL from their OIDC session
 * (requires sign-in via `linkedin_oidc`), scrapes the profile via LinkdAPI,
 * and matches one of the listed roles against the directory row's
 * `linkedin` field (URL slug) or `name` (normalized fallback). One-shot —
 * no separate redeem step.
 *
 * Throws when:
 *   - User isn't signed in via LinkedIn → friendly "sign in with LinkedIn first"
 *   - Scrape fails (LinkdAPI down / quota) → 502
 *   - No matching role → 403 with `companyName` in the message
 */
export async function requestLinkedInProof(args: {
  directoryId: string;
}): Promise<LinkedInProofResponse> {
  const { data, error, code } = await callEdgeFunction<LinkedInProofResponse>(
    'verify-company-claim',
    { action: 'request_linkedin_proof', ...args },
  );
  if (error) throw refusal(error, code);
  if (!data) throw new Error('Empty response from verify-company-claim');
  return data;
}
